import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CreditCard, Check, Loader, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BurgerMenu from './BurgerMenu';
import BackButton from './BackButton';
import { SUPABASE_CONFIG } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

interface CreditPackage {
  id: string;
  credits: number;
  price: number;
  popular?: boolean;
  paddle_price_id_sandbox?: string;
  paddle_price_id?: string;
}

interface ActiveSubscription {
  id: string;
  plan_id: string;
  status: string;
  subscription_code?: string;
  payment_plans: {
    id: string;
    plan_code: string;
    name: string;
    tour_credits: number;
    amount: number;
  };
}

const BuyCredits: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [purchaseType, setPurchaseType] = useState<'subscription' | 'once-off'>(
    location.state?.tab === 'subscription' ? 'subscription' : 'once-off'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMessage, setShowMessage] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState<ActiveSubscription | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  
  // Get message from navigation state (if redirected due to insufficient credits)
  const stateMessage = location.state?.message;
  
  // Show message notification when component mounts if there's a message
  useEffect(() => {
    if (stateMessage) {
      setShowMessage(true);
      // Auto-hide after 3 seconds
      const timer = setTimeout(() => setShowMessage(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [stateMessage]);

  // Fetch active subscription
  useEffect(() => {
    const fetchActiveSubscription = async () => {
      if (!user) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await fetch(
          `${SUPABASE_CONFIG.url}/functions/v1/get-active-subscription?user_id=${user.id}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const data = await response.json();

        if (data.status === 'success' && data.has_active_subscription) {
          setActiveSubscription(data.subscription);
          console.log('📋 Active subscription:', data.subscription);
        } else {
          setActiveSubscription(null);
        }
      } catch (error) {
        console.error('Error fetching active subscription:', error);
        setActiveSubscription(null);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchActiveSubscription();
  }, [user]);

  // Auto-select active subscription when switching to subscription tab
  useEffect(() => {
    if (purchaseType === 'subscription' && activeSubscription) {
      setSelectedPackage(activeSubscription.payment_plans.plan_code);
    }
  }, [purchaseType, activeSubscription]);
  
  const onceOffPackages: CreditPackage[] = [
    { id: 'once_1_tour', credits: 1, price: 12, paddle_price_id_sandbox: 'pri_01kee9thwwbafx761fwjf8whh2' },
    { id: 'once_3_tours', credits: 3, price: 30, popular: true },
    { id: 'once_5_tours', credits: 5, price: 45 }
  ];

  const subscriptionPackages: CreditPackage[] = [
    { id: 'monthly_2_tours', credits: 2, price: 18 },
    { id: 'monthly_5_tours', credits: 5, price: 40, popular: true },
    { id: 'monthly_10_tours', credits: 10, price: 85 }
  ];

  const currentPackages = purchaseType === 'subscription' ? subscriptionPackages : onceOffPackages;

  const handleCancelSubscription = async () => {
    if (!activeSubscription || !user) return;

    const confirmCancel = window.confirm(
      `Are you sure you want to cancel your ${activeSubscription.payment_plans.name} subscription? You will still have access until the end of your billing period.`
    );

    if (!confirmCancel) return;

    setCancelling(true);
    setError(null);

    try {
      console.log('🚫 Cancelling subscription:', activeSubscription.id);

      const response = await fetch(
        `${SUPABASE_CONFIG.url}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user.id,
            subscription_id: activeSubscription.id,
          }),
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        console.log('✅ Subscription cancelled successfully');
        setActiveSubscription(null);
        
        // Refresh credits
        if ((window as any).refreshCredits) {
          (window as any).refreshCredits();
        }

        alert('Your subscription has been cancelled successfully. You will still have access to your remaining credits.');
      } else {
        throw new Error(data.error || 'Failed to cancel subscription');
      }
    } catch (error: any) {
      console.error('Subscription cancellation failed:', error);
      setError(error.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    
    const selectedPlan = currentPackages.find(pkg => pkg.id === selectedPackage);
    if (!selectedPlan) return;

    // Ensure user is authenticated
    if (!user) {
      setError('You must be logged in to make a purchase');
      navigate('/');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if Paddle is loaded
      if (!(window as any).Paddle) {
        throw new Error('Paddle payment system not loaded. Please refresh the page.');
      }

      // Get the correct Price ID based on environment
      // For now, always use sandbox Price ID since PADDLE_ENVIRONMENT=sandbox
      const priceId = (selectedPlan as any).paddle_price_id_sandbox;

      if (!priceId || priceId.startsWith('SANDBOX_PRI_') || priceId.startsWith('PLACEHOLDER_')) {
        throw new Error('This payment plan is not yet configured. Please contact support.');
      }

      console.log('💳 Opening Paddle checkout for plan:', selectedPlan.id, 'Price ID:', priceId);

      // Open Paddle overlay checkout
      (window as any).Paddle.Checkout.open({
        items: [
          {
            priceId: priceId,
            quantity: 1,
          }
        ],
        customer: {
          email: user.email,
        },
        customData: {
          user_id: user.id,
          plan_code: selectedPackage,
          credits: selectedPlan.credits,
          purchase_type: purchaseType,
        },
      });

      setLoading(false);
    } catch (err: any) {
      console.error('Payment initialization failed:', err);
      setError(err.message || 'Failed to open checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="header">
        <BackButton onClick={() => navigate('/home')} />
        <h2 className="header-title">Buy Tours</h2>
        <BurgerMenu />
      </div>

      {/* Insufficient Credits Notification */}
      <AnimatePresence>
        {showMessage && stateMessage && (
          <>
            {/* Backdrop to capture clicks */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMessage(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9998,
              }}
            />
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'fixed',
                top: 'max(56px, calc(env(safe-area-inset-top) + 12px))',
                left: '0',
                right: '0',
                marginLeft: 'auto',
                marginRight: 'auto',
                zIndex: 9999,
                width: 'calc(100% - 32px)',
                maxWidth: '500px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                padding: '16px 20px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <AlertCircle size={20} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                  Insufficient Credits
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9, whiteSpace: 'pre-line' }}>
                  {stateMessage}
                </div>
              </div>
              <button
                onClick={() => setShowMessage(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: '1',
                }}
              >
                ×
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-4">
            <h3>Choose Your Package</h3>
            <p>{purchaseType === 'subscription' ? 'Monthly recurring credits, cancel anytime' : 'Tours never expire and can be used anytime'}</p>
          </div>

          {/* Segmented Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            style={{
              position: 'relative',
              display: 'flex',
              backgroundColor: 'var(--card-background)',
              borderRadius: '12px',
              padding: '4px',
              marginBottom: '24px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--border-color)'
            }}
          >
            {/* Animated background that slides */}
            <motion.div
              layoutId="segmented-toggle-bg"
              style={{
                position: 'absolute',
                top: '4px',
                bottom: '4px',
                width: 'calc(50% - 4px)',
                backgroundColor: 'var(--primary-color)',
                borderRadius: '8px',
                zIndex: 0
              }}
              initial={false}
              animate={{
                left: purchaseType === 'once-off' ? '4px' : 'calc(50% + 0px)'
              }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
            
            <button
              onClick={() => {
                setPurchaseType('once-off');
                setSelectedPackage('');
              }}
              style={{
                flex: 1,
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                backgroundColor: 'transparent',
                color: purchaseType === 'once-off' ? 'white' : 'var(--text-primary)',
                position: 'relative',
                zIndex: 1
              }}
            >
              Once off
            </button>
            <button
              onClick={() => {
                setPurchaseType('subscription');
                // Auto-select active subscription if exists, otherwise clear selection
                if (activeSubscription) {
                  setSelectedPackage(activeSubscription.payment_plans.plan_code);
                } else {
                  setSelectedPackage('');
                }
              }}
              style={{
                flex: 1,
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                backgroundColor: 'transparent',
                color: purchaseType === 'subscription' ? 'white' : 'var(--text-primary)',
                position: 'relative',
                zIndex: 1
              }}
            >
              Subscription
            </button>
          </motion.div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
            {currentPackages.map((pkg: CreditPackage, index: number) => {
              // Check if this package is the active subscription
              const isActiveSubscription = 
                purchaseType === 'subscription' && 
                activeSubscription && 
                activeSubscription.payment_plans.plan_code === pkg.id;

              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`card-motion ${selectedPackage === pkg.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPackage(selectedPackage === pkg.id ? '' : pkg.id)}
                  style={{
                    cursor: 'pointer',
                    border: isActiveSubscription 
                      ? '2px solid #10B981'
                      : selectedPackage === pkg.id ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                    position: 'relative',
                    backgroundColor: isActiveSubscription
                      ? 'rgba(16, 185, 129, 0.1)'
                      : selectedPackage === pkg.id ? 'var(--primary-color)' : 'var(--card-background)',
                    color: (selectedPackage === pkg.id && !isActiveSubscription) ? 'white' : 'var(--text-primary)',
                    borderRadius: 'var(--border-radius)',
                    padding: '20px',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                >
                <div className="flex-between">
                  <div>
                    <h3 style={{ 
                      fontSize: '22px', 
                      marginBottom: '4px',
                      color: (selectedPackage === pkg.id && !isActiveSubscription) ? 'white' : 'var(--text-primary)'
                    }}>
                      {pkg.credits} Tours{purchaseType === 'subscription' ? ' Monthly' : ''}
                    </h3>
                    <p style={{ 
                      fontSize: '14px', 
                      marginBottom: 0,
                      color: (selectedPackage === pkg.id && !isActiveSubscription) ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)'
                    }}>
                      ${(pkg.price / pkg.credits).toFixed(2)} per tour
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '22px', 
                      fontWeight: 700,
                      color: (selectedPackage === pkg.id && !isActiveSubscription) ? 'white' : 'var(--primary-color)'
                    }}>
                      ${pkg.price}{purchaseType === 'subscription' ? ' pm' : ''}
                    </div>
                  </div>
                </div>
                
                {/* Active Subscription Badge */}
                {isActiveSubscription && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      backgroundColor: '#10B981',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '600',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Check size={14} />
                    Active
                  </motion.div>
                )}

                {/* Selected Package Check Mark */}
                {selectedPackage === pkg.id && !isActiveSubscription && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid var(--background)',
                      zIndex: 10
                    }}
                  >
                    <Check size={18} color="white" />
                  </motion.div>
                )}
              </motion.div>
              );
            })}
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              color: '#991B1B',
              fontSize: '14px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          {/* Continue button - flows naturally below content */}
          <div style={{
            paddingBottom: 'max(20px, env(safe-area-inset-bottom))'
          }}>
            {/* Show Cancel Subscription if active subscription is selected */}
            {purchaseType === 'subscription' && 
             selectedPackage && 
             activeSubscription &&
             activeSubscription.payment_plans.plan_code === selectedPackage ? (
              <button 
                className="btn"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                style={{
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  opacity: cancelling ? 0.6 : 1
                }}
              >
                {cancelling ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Subscription'
                )}
              </button>
            ) : (
              <>
                <button 
                  className="btn btn-primary"
                  onClick={handlePurchase}
                  disabled={!selectedPackage || loading}
                style={{
                  opacity: (!selectedPackage || loading) ? 0.5 : 1,
                  cursor: (!selectedPackage || loading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    Opening Checkout...
                  </>
                ) : (
                  <>
                    <CreditCard size={20} />
                    Continue to Payment
                  </>
                )}
              </button>
              <div style={{
                textAlign: 'center',
                marginTop: '12px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                opacity: 0.7
              }}>
                <Link 
                  to="/refund-policy" 
                  style={{
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                >
                  Learn more about our refund policy
                </Link>
              </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BuyCredits;
